import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, ShoppingCart, Receipt, Wrench, Archive, Users, CalendarDays, Layers, ShieldCheck, LogOut, Menu, X, KeyRound, Settings, Inbox, Bot, GraduationCap,
} from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { PasswordDialog } from "@/components/PasswordDialog";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/api";

const NAV = [
  { group: "Pilotage", items: [{ to: "/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard, roles: ["*"] }] },
  { group: "Commercial", items: [
    { to: "/demandes", label: "Demandes du site", icon: Inbox, roles: ["commercial"] },
    { to: "/devis", label: "Devis", icon: FileText, roles: ["commercial"] },
    { to: "/commandes", label: "Commandes", icon: ShoppingCart, roles: ["commercial"] },
    { to: "/factures", label: "Factures", icon: Receipt, roles: ["commercial"] },
  ] },
  { group: "Agent IA", items: [
    { to: "/agent-ia", label: "Agent Facebook & chat", icon: Bot, roles: ["commercial"] },
    { to: "/inscriptions", label: "Inscriptions formation", icon: GraduationCap, roles: ["commercial"] },
  ] },
  { group: "Opérations", items: [
    { to: "/interventions", label: "Interventions", icon: Wrench, roles: ["technicien"] },
    { to: "/archives", label: "Archives", icon: Archive, roles: [] },
  ] },
  { group: "Personnel", items: [
    { to: "/employes", label: "Employés", icon: Users, roles: ["rh"] },
    { to: "/conges", label: "Congés", icon: CalendarDays, roles: ["rh"] },
  ] },
  { group: "Catalogue", items: [{ to: "/services", label: "Services", icon: Layers, roles: ["*"] }] },
  { group: "Administration", items: [
    { to: "/utilisateurs", label: "Utilisateurs", icon: ShieldCheck, roles: [] },
    { to: "/parametres", label: "Paramètres société", icon: Settings, roles: [] },
  ] },
];

const allowed = (user, roles) => user.role === "admin" || roles.includes("*") || roles.includes(user.role);

const Sidebar = ({ user, onNavigate }) => (
  <nav className="flex h-full flex-col bg-[#0F172A] text-slate-300">
    <div className="p-4">
      <div className="rounded-md bg-white p-2">
        <img src="/logo.png" alt="ACES" className="h-14 w-full object-contain" data-testid="sidebar-logo" />
      </div>
    </div>
    <div className="flex-1 space-y-5 overflow-y-auto px-3 py-2">
      {NAV.map((g) => {
        const items = g.items.filter((i) => allowed(user, i.roles));
        if (!items.length) return null;
        return (
          <div key={g.group}>
            <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{g.group}</p>
            {items.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onNavigate}
                data-testid={`nav-${to === "/tableau-de-bord" ? "dashboard" : to.slice(1)}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
                    isActive ? "bg-red-600 text-white" : "hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </div>
        );
      })}
    </div>
  </nav>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState(false);
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">
        <Sidebar user={user} />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">
            <Sidebar user={user} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl sm:px-8">
          <button className="rounded-md p-2 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <a href="/" target="_blank" rel="noreferrer" className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 hover:text-red-600 sm:block" data-testid="header-public-site-link">
            A.C.E.S S.A.R.L — Voir le site public ↗
          </a>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900" data-testid="current-user-name">{user.name}</p>
              <p className="text-[11px] uppercase tracking-wider text-red-600" data-testid="current-user-role">{ROLES[user.role]}</p>
            </div>
            {(user.role === "admin" || user.role === "commercial") && <NotificationBell />}
            <button onClick={() => setPwd(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-slate-100" data-testid="change-password-btn" title="Changer mon mot de passe">
              <KeyRound className="h-4 w-4" />
            </button>
            <button onClick={doLogout} className="rounded-md border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600" data-testid="logout-btn" title="Déconnexion">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10">
          <Outlet />
        </main>
        <PasswordDialog open={pwd} onClose={() => setPwd(false)} />
      </div>
    </div>
  );
}
